import React, { useState, useEffect } from 'react';
import { useParams , useNavigate } from 'react-router-dom';  // To get id from URL
import axios from 'axios';

const Prospects = () => {
    const { id } = useParams();  // Extracting id from URL if present
    const [prospects, setProspects] = useState([{ heading: '', description: '', id: null }]); // Include id in default state
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');  // New success state
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Fetch career prospects if id is present in the URL
    const fetchCareerProspects = async () => {
        if (id) {
            try {
                setLoading(true);
                const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/fetchCareerProspects`, {
                    career_id: id
                }, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Cookie': ''
                    }
                });

                if (response.data.success) {
                    // Populate prospects with ids from the API response
                    const fetchedProspects = response.data.data.map(prospect => ({
                        heading: prospect.heading,
                        description: prospect.description,
                        id: prospect.id // Ensure the id is populated here
                    }));
                    setProspects(fetchedProspects);
                } else {
                    setError('Failed to fetch career prospects.');
                }
            } catch (err) {
                console.error('Error fetching career prospects:', err);
            } finally {
                setLoading(false);
            }
        }
    };

    // Call the fetch function when the component mounts and if id is present
    useEffect(() => {
        fetchCareerProspects();
    }, [id]);

    const handleAddProspect = () => {
        const lastProspect = prospects[prospects.length - 1];
        if (!lastProspect.heading || !lastProspect.description) {
            setError('Please fill both Heading and Description before adding another prospect.');
            return;
        }
        if (prospects.length < 10) {
            setProspects([...prospects, { heading: '', description: '', id: null }]); // Include id: null for new prospects
            setError('');
        } else {
            setError('You can only add up to 10 prospects.');
        }
    };

    const handleRemoveProspect = async (index) => {
        const prospectToRemove = prospects[index];

        // If the prospect has an id, make the API call to remove it
        if (prospectToRemove.id) {
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/removeProspects`, {
                    id: prospectToRemove.id
                }, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Cookie': 'connect.sid=s%3AH4fx_T-dcU6ZBIaCQsPzuexSifph9G2i.BbnPGj2NLRwTh2f8p2tBc7eIfSTEPRN0seQuUfeWG4Y'
                    }
                });

                console.log('Response from removeProspects:', response.data);

                if (response.data.success) {
                    setError('');
                } else {
                    setError('Failed to remove prospect.');
                    return;
                }
            } catch (error) {
                console.error('Error removing prospect:', error);
                setError('An error occurred while removing the prospect.');
                return;
            }
        }

        // Remove the prospect from the list in the UI
        const updatedProspects = prospects.filter((_, i) => i !== index);
        setProspects(updatedProspects);
    };

    const handleInputChange = (index, event) => {
        const { name, value } = event.target;
        const updatedProspects = [...prospects];
        updatedProspects[index][name] = value;
        setProspects(updatedProspects);
        setError('');  // Clear error when user starts typing
    };

    const handleSave = async () => {
        // Validate that all prospects have both heading and description filled
        for (let i = 0; i < prospects.length; i++) {
            if (!prospects[i].heading || !prospects[i].description) {
                setError(`Please fill both Heading and Description for prospect ${i + 1}.`);
                return;
            }
        }

        try {
            // Loop through each prospect and make API call
            for (let prospect of prospects) {
                const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/careerProspects`, {
                    career_id: id, // Sending the 'id' from the URL or another source
                    heading: prospect.heading,
                    description: prospect.description,
                    id: prospect.id || '0',
                    isEdit: prospect.id ? '1' : '0'
                }, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Cookie': ''
                    }
                });

                console.log(`Response for prospect with heading "${prospect.heading}":`, response.data);
            }

            setSuccess('Prospects saved successfully!');
            setError(''); 
            window.location.href = `/career-deep-dive/${id}?tab=pros_cons`;
            
        } catch (error) {
            console.error('Error saving prospects:', error);
            setError('An error occurred while saving prospects. Please try again.');
            setSuccess('');  // Clear success message on error
        }
    };

    const handleCancel = () => {
        navigate("/career-list");
    };

    return (
        <div className="bg-white radius-10 p-4 p-sm-5 shadow">
            <div className="row g-4">
                <div className="col-md-12">
                    <h2 className="fw-semibold font-24">Career Prospect</h2>
                </div>

                {/* Existing logic for prospects form */}
                {prospects.map((prospect, index) => (
                    <React.Fragment key={index}>
                        <div className="col-md-6">
                            <label htmlFor={`heading-${index}`} className="form-label w-100 font-500 mb-1">Heading</label>
                            <input
                                id={`heading-${index}`}
                                name="heading"
                                type="text"
                                className="form-control height-45"
                                placeholder="Ex: 3 Yrs"
                                maxLength="50"
                                value={prospect.heading}
                                onChange={(e) => handleInputChange(index, e)}
                            />
                        </div>
                        <div className="col-md-12">
                            <label htmlFor={`description-${index}`} className="form-label w-100 font-500 mb-1">Description</label>
                            <textarea
                                className="form-control h-auto"
                                placeholder="Leave a comment here"
                                id={`description-${index}`}
                                name="description"
                                rows="5"
                                value={prospect.description}
                                onChange={(e) => handleInputChange(index, e)}
                            />
                        </div>
                        {prospects.length > 1 && (
                            <div className="col-md-12 text-end">
                                <button
                                    type="button"
                                    className="btn btn-danger mt-2"
                                    onClick={() => handleRemoveProspect(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </React.Fragment>
                ))}

                {error && <div className="col-12 text-danger">{error}</div>}
                {loading && <div className="col-12">Loading...</div>}

                <div className="col-12">
                    <hr className="mt-0" />
                    <button className="btn-green pe-3" onClick={handleAddProspect}>
                        <i className="icon-plus me-2"></i> Add More Prospect
                    </button>
                </div>
                <div className="col-md-12">
                    <div className="action_btns mt-3">
                        <div className="row g-2 align-items-center justify-content-center">
                            <div className="col-auto">
                                <button type="button" className="btn btn-green height-45 px-4" onClick={handleSave}>
                                    <span>Save and Next</span>
                                </button>
                            </div>
                            <div className="col-auto">
                                <button type="button" className="btn btn-border-gray height-45 px-5" onClick={handleCancel}>
                                    <span>Cancel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    {success && <div className="alert alert-success mt-3">{success}</div>}
                </div>
            </div>
        </div>
    );
};

export default Prospects;
